// @flow

import React, { Component } from 'react';
import { withTranslation, WithTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';

import userService from '../../services/user-service';
import recordSharingService from '../../services/record-sharing-service';
import UserForm from '../user-form';
import UserFormModel from '../../models/ProfileForm';
import AddressForm from '../address-form';
import AddressFormModel from '../../models/AddressForm';
import type { UserIncludeAddress } from '../../types/user';
import AppView from '../layouts/AppView';
import ForgotPassword from '../change-password';
import authService from '../../services/auth-service';
import Text from '../shared/Typography';
import Section from '../shared/PageSection';
import CardSplit from '../shared/CardSplit';
import MembershipCard from '../membership-card';
import { formatDate } from '../../utils/format';
import userActions from '../../actions/user';
import { getRouteTFunction } from '../../i18n';

type AuthUserType = {
  email: string,
  email_verified: boolean,
  tfa_enabled: boolean,
  created_at: string,
  last_ip: string
}

type State = {
  user: UserFormModel,
  address: AddressFormModel,
  error: string,
  loading: boolean,
  email: string,
  userMessage: string,
  addressMessage: string,
  authUser: ?AuthUserType,
  shares?: []
}

type Props = WithTranslation

class UserProfile extends Component<Props, State> {
  state: State = {
    user: new UserFormModel(),
    address: new AddressFormModel(),
    authUser: null,
    email: '',
    error: '',
    loadingProfile: false,
    loadingAddress: false,
    loadingPwd: false,
    profileMessage: '',
    addressMessage: '',
    pwdMessage: '',
    shares: [],
    disabledUserFields: ['Email', 'HealthNumber']
  }

  props: Props
  componentDidMount() {
    if (
      this.props.user &&
      this.props.user.onboarding_state &&
      this.props.user.onboarding_state.state !== 'UserCreated'
    ) {
      this.fetchData();
      this.fetchSharedResources();
      this.fetchUserInfo();
    } else {
      const routesTFunction = getRouteTFunction();
      this.props.history.push(routesTFunction('redirectPaths./user/info'));
    }
  }

  fetchUserInfo() {
    authService.getUserInfo().then((response) => {
      this.setState({
        authUser: response.user
      });
    });
  }

  fetchData(): Promise<void> {
    return userService.show('?include=insurance,address,region,photo_id')
      .then((user: UserIncludeAddress) => {
        let updatedUser = { ...user };
        if (user.insurance && user.insurance.insurance_number) {
          updatedUser = {
            ...updatedUser,
            insurance_number: user.insurance.insurance_number
          };
        }
        if (user.region) {
          updatedUser = {
            ...updatedUser,
            region_id: user.region.id
          };
        }

        const { disabledUserFields } = this.state;
        if (user.dob && user.dob.length) {
          disabledUserFields.push('Birthdate');
        }

        if (user.photo_id && user.photo_id[0] && user.photo_id[0].status === 'verified') {
          disabledUserFields.push('FirstName', 'LastName');
        }

        this.setState({
          user: new UserFormModel(updatedUser),
          address: new AddressFormModel({
            ...user.address
          }),
          email: user.email,
          emailVerified: user.email_verified,
          loadingAddress: false,
          memberStatus: user.member_status,
          disabledUserFields: disabledUserFields
        });
      });
  }

  fetchSharedResources(): Promise<void> {
    return recordSharingService.getAllShares().then((response) => {
      this.setState({
        shares: response
      });
    });
  }

  updateNewUser(name: string, value: ?string): void {
    const newUser = new UserFormModel(Object.assign({}, this.state.user.values, {
      [name]: value
    }));
    this.setState({
      user: newUser
    });
  }

  updateNewAddress(name: string, value: ?string): void {
    const newAddress = new AddressFormModel(Object.assign({}, this.state.address.values, {
      [name]: value
    }));
    this.setState({
      address: newAddress
    });
  }

  userFormSubmit = (evt: Event): void => {
    evt.preventDefault();
    this.setState({ loadingProfile: true });
    this.state.user.validate();
    const disregardInsuranceError = this.state.user.hasErrors
      && Object.keys(this.state.user.errors).length === 1
      && this.state.user.errors.insurance_number
      && !this.state.user.values.insurance_number;
    if (this.state.user.hasErrors && !disregardInsuranceError) {
      this.setState({
        loadingProfile: false
      });
    } else {
      userService.edit(this.state.user.values).then(() => {
        this.fetchData().then(() => {
          this.setState({
            profileMessage: this.props.t('updated_your_info'),
            loadingProfile: false
          });
        });
      }).catch((error) => {
        this.setState({
          profileError: error,
          loadingProfile: false
        });
      });
    }
  }

  addressFormSubmit = (evt: Event): void => {
    evt.preventDefault();
    this.setState({ loadingAddress: true });
    this.state.address.validate();
    if (this.state.address.hasErrors) {
      this.setState({
        loadingAddress: false
      });
    } else {
      userService.updateAddress(this.state.address.values).then(() => {
        this.fetchData().then(() => {
          this.setState({
            addressMessage: this.props.t('updated_your_addr')
          });
        });
      }).catch((error) => {
        this.setState({
          AddressError: error,
          loadingAddress: false
        });
      });
    }
  }

  revokeShare = (shareId: Number) => {
    return recordSharingService.revoke(shareId, { revoked: true }).then((response) => {
      const shares = this.state.shares;
      const idToUpdate = shares.map((share) => share.id).indexOf(shareId);
      shares[idToUpdate].revoked = response.viewer.revoked;
      this.setState({
        shares
      });
    });
  }

  userValueChange = (name: string, value: string): void => {
    this.updateNewUser(name, value);
  }

  addressValueChange = (name: string, value: string): void => {
    this.updateNewAddress(name, value);
  }

  render() {
    const { user, memberStatus, shares } = this.state;
    const { i18n, t } = this.props;
    const userName = `${user.values.first_name} ${user.values.last_name}`;
    const free = memberStatus === 'free';
    return (
      <AppView viewType='private-body' pageClassName='user-profile'>
        <Section title={userName} icon='profile-illo' iconSize='large'>
          <main className='user-profile__main'>

            {/* MEMBERSHIP */}
            <MembershipCard free={free} />
            {/* MY PROFILE */}
            <CardSplit
              header={<Text icon='star' iconSize='medium' h2 bold>{t('my_profile')}</Text>}
            >
              <UserForm
                user={this.state.user.values}
                errors={this.state.user.errors}
                email={this.state.email}
                emailVerified={this.state.emailVerified}
                onSubmit={this.userFormSubmit}
                onValueChange={this.userValueChange}
                cta={t('update_my_info')}
                isLoading={this.state.loadingProfile}
                successMsg={this.state.profileMessage}
                disabledInputs={this.state.disabledUserFields}
                hideInsuranceField={!this.state.user.values.insurance_number}
              />
            </CardSplit>

            {/* ADDRESS */}
            <CardSplit
              header={<Text icon='building' iconSize='medium' h2 bold>{t('my_addr')}</Text>}
            >
              <AddressForm
                address={this.state.address.values}
                errors={this.state.address.errors}
                onSubmit={this.addressFormSubmit}
                onValueChange={this.addressValueChange}
                cta={t('update_my_addr')}
                isLoading={this.state.loadingAddress}
                successMsg={this.state.addressMessage}
              />
              <Text paragraph id='flash' className='is-error'>{this.state.error}</Text>
            </CardSplit>

            {/* SHARED RECORDS */}
            <CardSplit
              header={<Text icon='green-arrow-share' iconSize='medium' h2 bold>{t('shared_records')}</Text>}
              className='user-profile__shared-records'
            >
              {shares && shares.length > 0 &&
                <ul>
                  {
                    shares.map((share) => (
                      <li key={share.id}>
                        <div>
                          <Text paragraph className='count' color='ship-cove'>
                            {t('shared_n_records', { count: share.resources.length })}
                          </Text>
                          <Text paragraph color='ship-cove'>{share.email}</Text>
                          <Text paragraph>
                            {`${share.expired ? t('expired')
                              : t('expires')} ${formatDate(share.expiry_date, i18n.language)}`
                            }
                          </Text>
                          {share.revoked &&
                            <span className='record-share__expired record-share-action'>
                              {t('revoked')}
                            </span>
                          }
                          {share.expired && !share.revoked &&
                            <span className='record-share__expired record-share-action'>
                              {t('expired')}
                            </span>
                          }
                          {!share.expired && !share.revoked &&
                            <span
                              role='button'
                              className='type-link record-share__revoke record-share-action'
                              onClick={() => this.revokeShare(share.id)}
                              tabIndex={0}
                            >
                              {t('revoke')}
                            </span>
                          }
                        </div>
                      </li>
                    ))
                  }
                </ul>
              }
              {(!shares || !shares.length) &&
                <Text paragraph>{t('have_not_shared_yet')}</Text>
              }
            </CardSplit>
            {/* SECURITY */}
            <CardSplit
              header={<Text icon='green-lock' iconSize='medium' h2 bold>{t('security')}</Text>}
              className='user-profile__security'
            >
              <Text paragraph bold>{t('forgot_password')}</Text>
              <ForgotPassword
                email={this.state.email}
                isLoading={this.state.loadingPwd}
                successMsg={this.state.pwdMessage}
              />
            </CardSplit>
          </main>
        </Section>
      </AppView>
    );
  }
}

const mapStateToProps = (state) => ({
  user: state.user.data
});

export default withRouter(
  withTranslation('userProfile')(connect(mapStateToProps, { routeUser: userActions.routeUser })(UserProfile))
);



// WEBPACK FOOTER //
// ./src/components/pages/user-profile.js